import { jsPDF } from 'jspdf';

const escapeCsvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const formatCsvAmount = (value) => {
  const amount = Number(value || 0);
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return amount < 0 ? `-${formatted}` : formatted;
};

const createCsvBlob = (rows) => {
  const content = `\uFEFF${rows
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\r\n')}`;

  return new Blob([content], { type: 'text/csv;charset=utf-8;' });
};

const downloadBlob = (blob, fileName) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const toPdfText = (value) => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const formatDate = (value) => {
  if (!value) return '';

  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatStatementAmount = (value) => {
  const amount = Number(value || 0);
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return amount < 0 ? `(${formatted})` : formatted;
};

const getPercentage = (value, income) => {
  const totalIncome = Number(income || 0);

  if (totalIncome === 0) return '0.00%';

  return `${((Number(value || 0) / totalIncome) * 100).toFixed(2)}%`;
};

const getVisibleDetailAccounts = (reportData, showZeroBalances) => {
  const sections = ['income', 'costOfGoodsSold', 'expenses'];

  return sections.reduce((result, section) => {
    result[section] = (reportData.accountDetails?.[section] || []).filter((account) => (
      showZeroBalances || Number(account.amount || 0) !== 0
    ));
    return result;
  }, {});
};

const drawAmount = (pdf, value, x, y, fontSize = 9, bold = false) => {
  const amount = Number(value || 0);

  pdf.setFont('helvetica', bold ? 'bold' : 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(amount < 0 ? 180 : 35, amount < 0 ? 45 : 45, amount < 0 ? 45 : 45);
  pdf.text(formatStatementAmount(amount), x, y, { align: 'right' });
};

const drawFooter = (pdf, pageWidth, pageHeight, margin, generatedAt) => {
  const footerY = pageHeight - 26;

  pdf.setDrawColor(210, 215, 212);
  pdf.setLineWidth(0.5);
  pdf.line(margin, footerY - 10, pageWidth - margin, footerY - 10);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(105, 112, 108);
  pdf.text(`Generated ${generatedAt}`, margin, footerY);
  pdf.text(
    `Page ${pdf.internal.getNumberOfPages()} of {totalPages}`,
    pageWidth - margin,
    footerY,
    { align: 'right' }
  );
};

const drawSectionHeader = (pdf, title, x, y, width) => {
  pdf.setFillColor(232, 242, 235);
  pdf.setDrawColor(198, 218, 202);
  pdf.setLineWidth(0.5);
  pdf.rect(x, y - 12, width, 19, 'FD');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(38, 89, 53);
  pdf.text(title.toUpperCase(), x + 7, y + 1);
};

const drawStatementLine = (pdf, label, value, x, y, width, options = {}) => {
  const {
    indent = 14,
    bold = false,
    highlight = false,
    fontSize = 9
  } = options;

  if (highlight) {
    pdf.setFillColor(245, 249, 246);
    pdf.rect(x, y - 11, width, 18, 'F');
  }

  pdf.setFont('helvetica', bold ? 'bold' : 'normal');
  pdf.setFontSize(fontSize);
  pdf.setTextColor(35, 45, 38);
  pdf.text(String(label ?? ''), x + indent, y);
  drawAmount(pdf, value, x + width - 8, y, fontSize, bold);

  pdf.setDrawColor(226, 231, 227);
  pdf.setLineWidth(0.35);
  pdf.line(x + indent, y + 5, x + width - 8, y + 5);
};

const drawTotalLine = (pdf, label, value, x, y, width, options = {}) => {
  const { prominent = false, highlight = false } = options;

  if (highlight) {
    pdf.setFillColor(238, 247, 240);
    pdf.rect(x, y - 14, width, prominent ? 25 : 21, 'F');
  }

  pdf.setDrawColor(prominent ? 95 : 145, prominent ? 135 : 163, prominent ? 104 : 149);
  pdf.setLineWidth(prominent ? 0.9 : 0.6);
  pdf.line(x, y - 9, x + width, y - 9);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(prominent ? 11 : 9);
  pdf.setTextColor(25, 48, 31);
  pdf.text(String(label ?? ''), x + 2, y + 4);
  drawAmount(pdf, value, x + width - 8, y + 4, prominent ? 11 : 9, true);
};

const buildDetailSections = (reportData, showZeroBalances) => {
  const accounts = getVisibleDetailAccounts(reportData, showZeroBalances);

  return [
    {
      title: 'Income',
      accounts: accounts.income,
      totalLabel: 'Total Income',
      total: reportData.income.total
    },
    {
      title: 'Cost of Goods Sold',
      accounts: accounts.costOfGoodsSold,
      totalLabel: 'Total Cost of Goods Sold',
      total: reportData.costOfGoodsSold.total
    },
    {
      title: 'Operating Expenses',
      accounts: accounts.expenses,
      totalLabel: 'Total Operating Expenses',
      total: reportData.expenses.total
    }
  ];
};

export const downloadProfitLossCsv = ({
  companyName,
  dates,
  basis,
  reportData,
  viewMode = 'summary',
  showZeroBalances = false,
  fileName
}) => {
  const periodLabel = `${formatDate(dates.startDate)} to ${formatDate(dates.endDate)}`;
  const rows = [
    ['Statement of Income and Expenses', '', ''],
    [companyName || 'Accounting Workspace', '', ''],
    [`Date Range: ${periodLabel}`, '', ''],
    [`Accounting Method: ${basis === 'cash' ? 'Cash' : 'Accrual'}`, '', ''],
    ['', '', '']
  ];

  if (viewMode === 'summary') {
    rows.push(
      ['Income', '', ''],
      ['', 'Total Income', formatCsvAmount(reportData.income.total)],
      ['', '', ''],
      ['Cost of Goods Sold', '', ''],
      ['', 'Total Cost of Goods Sold', formatCsvAmount(reportData.costOfGoodsSold.total)],
      ['', 'Gross Profit', formatCsvAmount(reportData.grossProfit)],
      ['', 'Percentage of Total Income', getPercentage(
        reportData.grossProfit,
        reportData.income.total
      )],
      ['', '', ''],
      ['Operating Expenses', '', ''],
      ['', 'Total Operating Expenses', formatCsvAmount(reportData.expenses.total)],
      ['', 'Net Profit (Loss)', formatCsvAmount(reportData.netIncome)],
      ['', 'Percentage of Total Income', getPercentage(
        reportData.netIncome,
        reportData.income.total
      )]
    );
  } else {
    buildDetailSections(reportData, showZeroBalances).forEach((section) => {
      rows.push(['', section.title, '']);

      section.accounts.forEach((account) => {
        rows.push([
          account.code || '',
          account.name || '',
          formatCsvAmount(account.amount)
        ]);
      });

      rows.push(
        ['', section.totalLabel, formatCsvAmount(section.total)],
        ['', '', '']
      );
    });

    rows.push(
      ['', 'Gross Profit', formatCsvAmount(reportData.grossProfit)],
      ['', 'Net Profit (Loss)', formatCsvAmount(reportData.netIncome)]
    );
  }

  downloadBlob(createCsvBlob(rows), fileName);
};

export const downloadProfitLossPdf = ({
  companyName,
  dates,
  basis,
  reportData,
  viewMode = 'summary',
  showZeroBalances = false,
  fileName,
  logoUrl
}) => {
  if (!reportData) {
    throw new Error('No income or expense activity found for the selected period.');
  }

  const PdfConstructor = typeof jsPDF === 'function'
    ? jsPDF
    : window.jspdf?.jsPDF;

  if (typeof PdfConstructor !== 'function') {
    throw new Error('PDF export is unavailable. Please refresh the page and try again.');
  }

  const pdf = new PdfConstructor({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
    compress: true
  });

  const originalPdfText = pdf.text.bind(pdf);
  pdf.text = (text, ...args) => {
    if (Array.isArray(text)) {
      text = text.map((value) => toPdfText(value));
    } else if (typeof text !== 'string') {
      text = toPdfText(text);
    }

    return originalPdfText(text, ...args);
  };

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 46;
  const contentWidth = pageWidth - margin * 2;
  const generatedAt = new Date().toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
  const periodLabel = `${formatDate(dates.startDate)} to ${formatDate(dates.endDate)}`;
  const statementSections = viewMode === 'summary'
    ? [
      {
        title: 'Income',
        accounts: [],
        totalLabel: 'Total Income',
        total: reportData.income.total
      },
      {
        title: 'Cost of Goods Sold',
        accounts: [],
        totalLabel: 'Total Cost of Goods Sold',
        total: reportData.costOfGoodsSold.total
      },
      {
        title: 'Operating Expenses',
        accounts: [],
        totalLabel: 'Total Operating Expenses',
        total: reportData.expenses.total
      }
    ]
    : buildDetailSections(reportData, showZeroBalances);

  let y = margin;

  const drawBrand = () => {
    if (logoUrl) {
      try {
        pdf.addImage(logoUrl, 'PNG', margin, y - 2, 30, 30);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(38, 89, 53);
        pdf.text('ProfitYo', margin + 38, y + 17);
      } catch {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(38, 89, 53);
        pdf.text('ProfitYo', margin, y + 14);
      }
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(38, 89, 53);
      pdf.text('ProfitYo', margin, y + 14);
    }
  };

  const drawPageChrome = () => {
    y = margin;
    drawBrand();
    y += 42;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(24, 35, 28);
    pdf.text(companyName || 'Accounting Workspace', pageWidth / 2, y, { align: 'center' });

    y += 21;
    pdf.setFontSize(12);
    pdf.text('Statement of Income and Expenses', pageWidth / 2, y, { align: 'center' });

    y += 18;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(75, 85, 78);
    pdf.text(`As of ${periodLabel}`, pageWidth / 2, y, { align: 'center' });

    y += 15;
    pdf.text(
      `Accounting method: ${basis === 'cash' ? 'Cash' : 'Accrual'}`,
      margin,
      y
    );
    pdf.text(
      `Generated: ${generatedAt}`,
      pageWidth - margin,
      y,
      { align: 'right' }
    );

    y += 17;
    pdf.setDrawColor(38, 89, 53);
    pdf.setLineWidth(1);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 20;
  };

  const drawStatementSection = (section) => {
    pdf.setFillColor(232, 242, 235);
    pdf.setDrawColor(198, 218, 202);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, y - 12, contentWidth, 19, 'FD');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(38, 89, 53);
    pdf.text(section.title.toUpperCase(), margin + 7, y + 1);
    y += 25;

    if (section.accounts.length === 0 && viewMode === 'details') {
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(8.5);
      pdf.setTextColor(125, 135, 128);
      pdf.text('No activity recorded for this period.', margin + 14, y);
      y += 18;
    } else {
      section.accounts.forEach((account) => {
        drawStatementLine(
          pdf,
          account.code ? `${account.code} · ${account.name}` : account.name,
          account.amount,
          margin,
          y,
          contentWidth
        );
        y += 18;
      });
    }

    drawTotalLine(pdf, section.totalLabel, section.total, margin, y, contentWidth);
    y += 27;
  };

  drawPageChrome();
  statementSections.forEach(drawStatementSection);

  drawTotalLine(pdf, 'Gross Profit', reportData.grossProfit, margin, y, contentWidth, {
    prominent: true,
    highlight: true
  });
  y += 34;

  drawTotalLine(
    pdf,
    'Total Expenses',
    Number(reportData.costOfGoodsSold.total || 0) + Number(reportData.expenses.total || 0),
    margin,
    y,
    contentWidth,
    { highlight: true }
  );
  y += 27;

  drawTotalLine(pdf, 'Net Profit / (Loss)', reportData.netIncome, margin, y, contentWidth, {
    prominent: true,
    highlight: true
  });

  if (viewMode === 'summary') {
    y += 34;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(80, 90, 83);
    pdf.text(
      `Gross Profit Percentage of Total Income: ${getPercentage(
        reportData.grossProfit,
        reportData.income.total
      )}`,
      margin + 2,
      y
    );
    y += 14;
    pdf.text(
      `Net Profit Percentage of Total Income: ${getPercentage(
        reportData.netIncome,
        reportData.income.total
      )}`,
      margin + 2,
      y
    );
  }

  drawFooter(pdf, pageWidth, pageHeight, margin, generatedAt);

  if (typeof pdf.putTotalPages === 'function') {
    pdf.putTotalPages('{totalPages}');
  }

  pdf.save(fileName);
};